import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { CheckCircle2, AlertTriangle } from "lucide-react";
import { getSupabaseClient } from "../providers/supabase/supabase";

// Marketing KPIs (docs/design mockup; spec §11) — computed from the activity +
// mail logs, never manual sheets. Stat tiles are hero numbers (not charts); the
// funnel is a single-hue magnitude bar. Conversation-rate uses a STATUS signal
// (icon + label, not color alone). offers/contracts/cost-per-contract are
// labelled "pending Stage 2" — not faked.

type Day = {
  day: string; dials: number; texts: number; emails: number; mail_pieces: number;
  conversations: number; correct_numbers: number; appointments: number;
  conversation_rate: number | null; min_conversation_rate: number; conversation_rate_ok: boolean;
};
type Funnel = {
  since: string; leads: number; qualified: number; appointments: number;
  offers: number; contracts: number; cost_per_contract: number | null; cost_source: string; stage2_pending: boolean;
};
type Dialer = {
  day: string; sessions: number; agents: number; dials: number; connects: number;
  dispositions: number; talk_time_s: number; connect_rate: number | null;
};
type DialerAgent = {
  agent_id: string; agent_name: string | null; sessions: number; dials: number;
  connects: number; talk_time_s: number; connect_rate: number | null;
};

const fmtTalk = (s: number) => `${Math.floor((s ?? 0) / 60)}m`;
const pct = (r: number | null | undefined) => (r != null ? `${Math.round(r * 100)}%` : "—");

const Tile = ({ label, value, sub }: { label: string; value: React.ReactNode; sub?: React.ReactNode }) => (
  <Card>
    <CardContent className="p-4">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="text-2xl font-bold mt-1 tracking-tight">{value}</div>
      {sub && <div className="text-xs mt-0.5">{sub}</div>}
    </CardContent>
  </Card>
);

const KpiPage = () => {
  const [d, setD] = useState<Day | null>(null);
  const [f, setF] = useState<Funnel | null>(null);
  const [dialer, setDialer] = useState<Dialer | null>(null);
  const [dialerAgents, setDialerAgents] = useState<DialerAgent[]>([]);

  useEffect(() => {
    getSupabaseClient().rpc("kpi_day").then(({ data }) => setD(data as Day));
    getSupabaseClient().rpc("kpi_funnel").then(({ data }) => setF(data as Funnel));
    getSupabaseClient().rpc("kpi_dialer_day").then(({ data }) => setDialer(data as Dialer));
    // Per-agent dialer rows for today (view is RLS-scoped: agents see their own, admins all).
    const todayUTC = new Date().toISOString().slice(0, 10);
    getSupabaseClient().from("kpi_dialer_daily").select("*").eq("day", todayUTC)
      .order("dials", { ascending: false })
      .then(({ data }) => setDialerAgents((data as DialerAgent[]) ?? []));
  }, []);

  const rate = d?.conversation_rate != null ? Math.round(d.conversation_rate * 100) : null;
  const minRate = d ? Math.round((d.min_conversation_rate ?? 0.15) * 100) : 15;
  const maxStep = f ? Math.max(f.leads, f.qualified, f.appointments, f.offers, f.contracts, 1) : 1;
  const usd = (n: number) => `$${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  // brand navy for the single-hue magnitude funnel (light/dark aware).
  const barColor = "bg-[#13294B] dark:bg-[#3E6488]";

  return (
    <div className="p-4 max-w-4xl">
      <h1 className="text-xl font-semibold">Marketing KPIs</h1>
      <div className="text-sm text-muted-foreground mb-4">Computed from the activity log — today &amp; month-to-date. Platform is the source of truth.</div>

      {/* Today's stat tiles */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
        <Tile label="Dials today" value={d?.dials ?? "—"} />
        <Tile label="Correct numbers" value={d?.correct_numbers ?? "—"} />
        <Tile label="Conversations" value={d?.conversations ?? "—"} sub={rate != null && <span className="text-muted-foreground">{rate}% of dials</span>} />
        <Tile label="Conversation rate" value={rate != null ? `${rate}%` : "—"}
          sub={rate != null && (
            d?.conversation_rate_ok
              ? <span className="inline-flex items-center gap-1 text-green-700 dark:text-green-400"><CheckCircle2 className="h-3.5 w-3.5" /> ≥ {minRate}% benchmark</span>
              : <span className="inline-flex items-center gap-1 text-amber-700 dark:text-amber-400"><AlertTriangle className="h-3.5 w-3.5" /> below {minRate}% — check list/number quality</span>
          )} />
        <Tile label="Appointments" value={d?.appointments ?? "—"} />
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {/* Funnel — single-hue magnitude bars, direct-labelled */}
        <Card>
          <CardHeader><CardTitle className="text-base">This month's funnel</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {/* Full funnel, end to end — Offers/Contracts now from real deal data (Stage 2b). */}
            {f && ([["Leads", f.leads], ["Qualified", f.qualified], ["Appointments", f.appointments], ["Offers", f.offers], ["Contracts", f.contracts]] as [string, number][]).map(([label, n]) => (
              <div key={label} className="flex items-center gap-3">
                <div className="w-24 text-xs text-muted-foreground">{label}</div>
                <div className="flex-1">
                  <div className={`h-7 rounded ${barColor} flex items-center px-2 text-white text-xs font-semibold`}
                    style={{ width: `${Math.max((n / maxStep) * 100, 8)}%` }}>{n}</div>
                </div>
              </div>
            ))}
            {f && (
              <div className="flex items-center justify-between border-t pt-2 mt-1 text-sm">
                <span className="text-muted-foreground">Cost per contract</span>
                {f.cost_per_contract != null
                  ? <span className="font-semibold">{usd(f.cost_per_contract)}</span>
                  : <span className="inline-flex items-center gap-1 text-amber-700 dark:text-amber-400"><AlertTriangle className="h-3.5 w-3.5" /> cost source pending</span>}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Today's channel activity */}
        <Card>
          <CardHeader><CardTitle className="text-base">Today's outreach</CardTitle></CardHeader>
          <CardContent className="space-y-2 text-sm">
            <Line label="Dials" value={d?.dials} />
            <Line label="Texts" value={d?.texts} />
            <Line label="Emails" value={d?.emails} />
            <Line label="Mail pieces" value={d?.mail_pieces} />
            <div className="text-xs text-muted-foreground pt-2">
              Offers→Contracts and cost-per-contract are live (Stage 2). Set month-to-date marketing spend on the Config page to compute cost-per-contract.
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Dialer performance (today) — session-level efficiency from dial_sessions. */}
      <Card className="mt-4">
        <CardHeader><CardTitle className="text-base">Power Dialer — today</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-3">
            <Tile label="Sessions" value={dialer?.sessions ?? "—"} sub={dialer?.agents ? <span className="text-muted-foreground">{dialer.agents} agents</span> : undefined} />
            <Tile label="Dials" value={dialer?.dials ?? "—"} />
            <Tile label="Connects" value={dialer?.connects ?? "—"} />
            <Tile label="Connect rate" value={pct(dialer?.connect_rate)} />
            <Tile label="Talk time" value={dialer ? fmtTalk(dialer.talk_time_s) : "—"} />
          </div>
          {dialerAgents.length > 0 && (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-muted-foreground text-left border-b">
                  <th className="py-1 font-medium">Agent</th>
                  <th className="py-1 font-medium text-right">Sessions</th>
                  <th className="py-1 font-medium text-right">Dials</th>
                  <th className="py-1 font-medium text-right">Connects</th>
                  <th className="py-1 font-medium text-right">Rate</th>
                  <th className="py-1 font-medium text-right">Talk</th>
                </tr>
              </thead>
              <tbody>
                {dialerAgents.map((a) => (
                  <tr key={a.agent_id} className="border-b last:border-0">
                    <td className="py-1">{a.agent_name || "—"}</td>
                    <td className="py-1 text-right">{a.sessions}</td>
                    <td className="py-1 text-right">{a.dials}</td>
                    <td className="py-1 text-right">{a.connects}</td>
                    <td className="py-1 text-right">{pct(a.connect_rate)}</td>
                    <td className="py-1 text-right">{fmtTalk(a.talk_time_s)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <div className="text-xs text-muted-foreground pt-2">
            Dials/connects here are the dialer's own session counters; the activity-log dials above include all calls.
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

const Line = ({ label, value }: { label: string; value?: number }) => (
  <div className="flex items-center justify-between border-b py-1 last:border-0">
    <span className="text-muted-foreground">{label}</span><span className="font-semibold">{value ?? "—"}</span>
  </div>
);

KpiPage.path = "/kpi";
export default KpiPage;
